"use client"

import { useState } from "react"
import { Button, Dropdown, Modal } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { Menu, X, LogOut, User } from "lucide-react"
import { LanguageSwitcher } from "../language-switcher"
import { useAuth } from "../../contexts/auth-context"
import { useNavigate } from "react-router-dom"

interface HeaderProps {
  isMobile: boolean
  mobileMenuOpen: boolean
  setMobileMenuOpen: (open: boolean) => void
}

export function Header({ isMobile, mobileMenuOpen, setMobileMenuOpen }: HeaderProps) {
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)

  const handleLogout = () => {
    setShowLogoutConfirmation(true)
  }

  const confirmLogout = async () => {
    try {
      await logout()
      setShowLogoutConfirmation(false)
    } catch (error) {
      console.error("Logout error:", error)
      setShowLogoutConfirmation(false)
    }
  }

  const cancelLogout = () => {
    setShowLogoutConfirmation(false)
  }

  const handleAccountClick = () => {
    navigate("/dashboard/account")
  }

  return (
    <header
      className="position-fixed top-0 start-0 end-0 d-flex align-items-center gap-3 border-bottom bg-white px-3 py-2"
      style={{ zIndex: 1020, height: "60px" }}
    >
      {/* Logo - Desktop only */}
      {!isMobile && (
        <div className="d-flex align-items-center gap-2">
          <img src="/logo.svg" alt="Unity Smart Living Logo" width={28} height={28} className="rounded" />
          <span className="fw-bold">Unity Smart Living</span>
        </div>
      )}

      {isMobile && (
        <div className="d-flex align-items-center">
          <Button variant="light" className="border-0 p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            <span className="visually-hidden">Toggle menu</span>
          </Button>
        </div>
      )}

      <div className="ms-auto d-flex align-items-center gap-3">
        <LanguageSwitcher />

        <Dropdown align="end">
          <Dropdown.Toggle
            as="div"
            className="d-flex align-items-center gap-2 cursor-pointer"
            style={{ cursor: "pointer" }}
          >
            {user?.profilePhotoUrl && (
              <img
                src={user.profilePhotoUrl || "/placeholder.svg"}
                alt={user.fullName}
                width={32}
                height={32}
                className="rounded-circle"
              />
            )}
            {!isMobile && <span className="small fw-medium">{user?.fullName}</span>}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item onClick={handleAccountClick}>
              <User className="me-2 size-4" />
              {t("account")}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout}>
              <LogOut className="me-2 size-4" />
              {t("logout")}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal show={showLogoutConfirmation} onHide={cancelLogout}>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmLogout")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("areYouSureLogout")}</Modal.Body>
        <Modal.Footer>
          <Button className="modal-cancel-btn" onClick={cancelLogout}>
            {t("cancel")}
          </Button>
          <Button className="modal-primary-btn" onClick={confirmLogout}>
            {t("logout")}
          </Button>
        </Modal.Footer>
      </Modal>
    </header>
  )
}
